import "./EmailVerification.css";
import Header from "../../Components/Shared/Header/Header";
import Footer from "../../Components/Shared/Footer/Footer";
import Modal from "../../Components/Modal/Modal";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import VivahApi from "../../api";
import { setLoadingMessage } from "../../utils/loading-store";

function EmailVerification() {
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [modal, setModal] = useState({ isOpen: false, type: 'info', title: '', message: '' });
  const [redirectTo, setRedirectTo] = useState(null);
  
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Get email from navigation state (passed from registration)
    if (location.state?.email) {
      setEmail(location.state.email);
    }
  }, [location]);

  // Handle redirect after modal closes
  useEffect(() => {
    if (redirectTo) {
      navigate(redirectTo.path, { state: redirectTo.state });
      setRedirectTo(null);
    }
  }, [redirectTo, navigate]);

  const closeModal = () => {
    setModal({ ...modal, isOpen: false });
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    
    if (!email || !code) {
      setError("Please enter both email and verification code");
      return;
    }

    if (code.length !== 6) {
      setError("Verification code must be 6 digits");
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");
    setLoadingMessage("Verifying email...");

    try {
      const response = await VivahApi.Auth.confirmSignup(email, code);
      
      setMessage(response.message || "Email verified successfully!");
      
      setModal({
        isOpen: true,
        type: 'success',
        title: 'Email Verified!',
        message: response.message || "Your email has been verified successfully. You can now log in."
      });
      
      // Set redirect destination
      setRedirectTo({ 
        path: "/login",
        state: { 
          message: "Email verified! Please login.",
          email: email 
        } 
      });
      
    } catch (err) {
      console.error("Verification error:", err);
      setError(err.message || "Verification failed. Please try again.");
      setModal({
        isOpen: true,
        type: 'error',
        title: 'Verification Failed',
        message: err.message || "Verification failed. Please try again."
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!email) {
      setError("Please enter your email address");
      return;
    }

    setResending(true);
    setError("");
    setMessage("");
    setLoadingMessage("Resending code...");

    try {
      const response = await VivahApi.Auth.resendCode(email);
      setMessage(response.message || "Verification code resent! Check your email.");
      setModal({
        isOpen: true,
        type: 'success',
        title: 'Code Sent!',
        message: response.message || "Verification code resent! Check your email."
      });
    } catch (err) {
      console.error("Resend error:", err);
      setError(err.message || "Failed to resend code. Please try again.");
      setModal({
        isOpen: true,
        type: 'error',
        title: 'Resend Failed',
        message: err.message || "Failed to resend code. Please try again."
      });
    } finally {
      setResending(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        confirmText="OK"
      />
      
      <div className="gla_page" id="gla_page">
        <a href="#gla_page" className="gla_top ti ti-angle-up gla_go"></a>
        <Header />
        
        <div
          className="gla_page_title gla_image_bck gla_wht_txt"
          data-color="#282828"
        >
          <div className="container text-left">
            <div className="row">
              <div className="col-md-8">
                <h1 className="gla_h1_title">Email Verification</h1>
              </div>
              <div className="col-md-4">
                <div className="breadcrumbs">
                  <a href="/">Home</a>
                  <span>Verify Email</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <section id="gla_content" className="gla_content">
          <section className="gla_section">
            <div className="container">
              <div className="row">
                <div className="col-md-6 col-md-push-3">
                  <div className="verification-card">
                    <h3 className="text-center">Verify Your Email</h3>
                    <p className="text-center verification-instruction">
                      We've sent a 6-digit verification code to your email address.
                      Please enter it below to complete your registration.
                    </p>

                    {message && (
                      <div className="alert alert-success" role="alert">
                        {message}
                      </div>
                    )}

                    {error && (
                      <div className="alert alert-danger" role="alert">
                        {error}
                      </div>
                    )}

                    <form onSubmit={handleVerify}>
                      <div className="form-group">
                        <label>Email Address*</label>
                        <input
                          type="email"
                          className="form-control form-opacity"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Enter your email"
                          required
                          disabled={loading}
                        />
                      </div>

                      <div className="form-group">
                        <label>Verification Code*</label>
                        <input
                          type="text"
                          className="form-control form-opacity verification-code-input"
                          value={code}
                          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="Enter code"
                          maxLength="6"
                          pattern="[0-9]{6}"
                          required
                          disabled={loading}
                        />
                        <small className="form-text text-muted">
                          Enter the 6-digit code sent to your email
                        </small>
                      </div>

                      <button
                        type="submit"
                        className="btn btn-primary btn-block"
                        disabled={loading || !email || code.length !== 6}
                      >
                        {loading ? "Verifying..." : "Verify Email"}
                      </button>
                    </form>

                    <div className="text-center verification-footer">
                      <p>Didn't receive the code?</p>
                      <button
                        type="button"
                        className="btn btn-link"
                        onClick={handleResendCode}
                        disabled={resending || !email}
                      >
                        {resending ? "Sending..." : "Resend Code"}
                      </button>
                    </div>

                    <div className="text-center">
                      <a href="/login" className="btn btn-link">
                        Back to Login
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </section>

        <Footer />
      </div>
    </>
  );
}

export default EmailVerification;
